// src/lib/holdSession.js
//
// The booking-session coordinator for holds.
//
//   * `beginHold` issues the ONE canonical hold request at the moment the
//     patient presses Continue and returns an operation object. The confirm
//     step consumes that same operation; it never starts a hold on mount.
//   * The countdown is derived only from the server's `expires_at` and
//     `server_now`. The clock offset is anchored to when the request was SENT,
//     so request/navigation latency and client clock skew can only make the
//     displayed time shorter, never longer, than the server's.
//   * A request that got no answer (timeout/network) or a 5xx is
//     INDETERMINATE: the hold may exist. `reconcileHold` reads the patient's
//     current hold (read-only GET) and, only for the same slot, re-issues the
//     token through the established secured POST recovery. It never blindly
//     creates a second hold.
//
// The raw hold token stays in memory with the operation. It is never written
// to web storage, a URL or a log.

import { patientBookingApi } from "../services/patientBooking";
import { isAbortError } from "./useSequencedRequest";

function track(op, promise, clock) {
  op.promise = promise.then(
    (data) => {
      op.state = "held";
      op.result = data;
      op.receivedAt = clock();
      return data;
    },
    (error) => {
      op.state = "failed";
      op.error = error;
      op.receivedAt = clock();
      throw error;
    }
  );
  // The consumer attaches its own handlers; this only prevents an
  // "unhandled rejection" if the patient leaves before it settles.
  op.promise.catch(() => {});
  return op;
}

export function beginHold(request, { api = patientBookingApi, clock = () => Date.now() } = {}) {
  const op = { kind: "create", request, requestedAt: clock(), state: "pending", result: null, error: null, receivedAt: null };
  return track(op, api.createHold(request), clock);
}

export function beginRecovery(holdId, request, { api = patientBookingApi, clock = () => Date.now() } = {}) {
  const op = { kind: "recover", request, requestedAt: clock(), state: "pending", result: null, error: null, receivedAt: null };
  return track(
    op,
    api.recoverHold(holdId, { serviceVersionId: request.serviceVersionId, displayTimezone: request.displayTimezone }),
    clock
  );
}

// True when the server may have created the hold even though we got an error.
export function isIndeterminate(error) {
  if (!error || isAbortError(error)) return false;
  if (!error.response) return true;
  return Number(error.response.status) >= 500;
}

// outcome: "recovered" (same slot, new token, ORIGINAL expiry) | "none" | "other"
export async function reconcileHold(request, { api = patientBookingApi, clock = () => Date.now() } = {}) {
  const current = await api.currentHold();
  const hold = current?.hold;
  if (!hold) return { outcome: "none" };
  if (hold.slot_inventory_id !== request.slotInventoryId) return { outcome: "other", hold };
  const op = beginRecovery(hold.hold_id, request, { api, clock });
  const data = await op.promise;
  return { outcome: "recovered", data, operation: op };
}

export function clockOffsetMs(serverNow, requestedAt) {
  if (!serverNow || !Number.isFinite(requestedAt)) return 0;
  const server = new Date(serverNow).getTime();
  return Number.isFinite(server) ? server - requestedAt : 0;
}

export function remainingSeconds(expiresAt, offsetMs, nowMs = Date.now()) {
  if (!expiresAt) return 0;
  const expiry = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiry)) return 0;
  return Math.max(0, Math.floor((expiry - (nowMs + offsetMs)) / 1000));
}

export function formatCountdown(seconds) {
  const safe = Math.max(0, Math.floor(seconds || 0));
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
}
